/**
 * Composant principal pour l'interface de pointage
 * @returns 
 */
import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { LogOut, LogIn } from "lucide-react";
import { PinKeypad } from "./PinKeypad";
import { SuccessMessage } from "./SuccessMessage";
import { useEmployees } from "@/hooks/useEmployees";
import { useClockInOut } from "@/hooks/useClockInOut";
import { getCompanyId } from "@/services/config.services";
import { useWebSocket } from "@/contexts/WebSocketConext";
import { Header } from "../global/Header";
import { Footer } from "../global/Footer";



export const TimeClock = () => {
  const [currentTime, setCurrentTime] = useState(new Date()); // date et heure actuelle
  const [showPinKeypad, setShowPinKeypad] = useState(false); // affichage du clavier PIN
  const [actionType, setActionType] = useState<"CLOCK_IN" | "CLOCK_OUT" | null>(
    null
  );
  const [error, setError] = useState(""); // message d'erreur

  const [successData, setSuccessData] = useState<{
    action: "CLOCK_IN" | "CLOCK_OUT";
    employeeName: string;
    duration?: number | null;
    time: string;
  } | null>(null);

  // État pour gérer le blocage de l'entreprise
  const { isCheckingStatus } = useWebSocket();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000); // mise à jour de l'heure toutes les secondes
    return () => clearInterval(timer);
  }, []);

  // useEffect(() => {
  //   const checkStatus = async () => {
  //     try {
  //       const status = await checkCompanyStatus();
  //       if (!status.isActive) {
  //         setIsBlocked(true);
  //       } else {
  //         setIsBlocked(false);
  //       }
  //     } catch (error) {
  //       // Ne pas bloquer en cas d'erreur (mode offline)
  //       console.warn("Impossible de vérifier le statut:", error);
  //       setIsBlocked(false); // ✅ Permettre l'utilisation
  //     } finally {
  //       setIsCheckingStatus(false);
  //     }
  //   };

  //   // Vérifier au démarrage
  //   checkStatus();

  //   // Vérifier toutes les 5 minutes (au lieu de 30 secondes)
  //   const interval = setInterval(checkStatus, 5 * 60 * 1000); // 5 minutes

  //   // Vérifier quand l'utilisateur revient sur l'app (tab devient visible)
  //   const handleVisibilityChange = () => {
  //     if (!document.hidden) {
  //       checkStatus();
  //     }
  //   };

  //   document.addEventListener("visibilitychange", handleVisibilityChange);

  //   // Vérifier quand l'utilisateur revient sur la fenêtre (focus)
  //   const handleFocus = () => {
  //     checkStatus();
  //   };

  //   window.addEventListener("focus", handleFocus);

  //   return () => {
  //     clearInterval(interval);
  //     document.removeEventListener("visibilitychange", handleVisibilityChange);
  //     window.removeEventListener("focus", handleFocus);
  //   };
  // }, []);

  // formatage de la date
  const formattedDate = currentTime.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  // formatage de l'heure
  const formattedTime = currentTime.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const clockMutation = useClockInOut();

  const { data: employeesData } = useEmployees();
  const employees = employeesData?.employees;

  // Gestion du pointage
  const handleClockAction = (action: "CLOCK_IN" | "CLOCK_OUT") => {
    setActionType(action);
    setShowPinKeypad(true);
    setError("");
  };

  // Validation du PIN
  const handlePinSubmit = async (pin: string) => {
    if (!navigator.onLine) {
      setError("Pas de connexion internet. Vérifiez votre réseau et réessayez.");
      return;
    }

    if (!actionType) return;

    // VALIDATION CÔTÉ CLIENT (avec les employés en cache)
    const employee = employees?.find((emp) => emp.pin === pin);

    if (!employee) {
      setError("PIN invalide");
      return;
    }

    const employeeName = `${employee.firstName} ${employee.lastName}`;

    const companyId = getCompanyId();
    if (!companyId) {
      setError("Pointeuse non configurée");
      return;
    }

    const currentAction = actionType;

    clockMutation.mutate(
      { pin, action: currentAction },
      {
        onSuccess: (result) => {
          setSuccessData({
            action: currentAction,
            employeeName,
            duration:
              currentAction === "CLOCK_OUT"
                ? result.data.workSession.duration
                : null,
            time: new Date().toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
          setShowPinKeypad(false);
          setError("");

          setTimeout(() => setSuccessData(null), 10000);
        },
        onError: (err) => {
          // Toute erreur (réseau, PIN invalide, etc.) reste affichée dans le clavier
          // pour permettre une nouvelle tentative manuelle.
          const message =
            err instanceof Error ? err.message : "Erreur lors du pointage";
          setError(message);
        },
      }
    );
  };


  // Afficher l'écran de blocage si bloqué
  if (isCheckingStatus) {
    return (
      <div className="fixed inset-0 min-h-screen min-w-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        </div>
      </div>
    );
  }

  
  return (
    <div className="h-full flex flex-col overflow-hidden bg-gray-50">
      <Header />
      <main className="flex-1 min-h-0 flex flex-col items-center justify-center px-6 py-6">
        {/* Date */}
        <p className="text-gray-600 text-primary font-bold text-lg mb-2 capitalize">{formattedDate}</p>

        {/* Clock */}
        <h1 className="text-6xl md:text-8xl font-bold text-primary mb-8 font-mono tracking-wider">
          {formattedTime}
        </h1>

        {/* Clock Buttons */}
        <div className="flex gap-6 w-full max-w-md">
          <Button
            onClick={() => handleClockAction("CLOCK_IN")}
            className="flex-1 h-20 text-xl font-semibold bg-primary hover:bg-primary/90 text-white shadow-lg"
          >
            <LogIn className="mr-3 h-6 w-6" />
            Arrivée
          </Button>

          <Button
            onClick={() => handleClockAction("CLOCK_OUT")}
            className="flex-1 h-20 text-xl font-semibold bg-white border-2 border-primary text-primary hover:bg-primary/10 shadow-lg"
          >
            Départ
            <LogOut className="mr-3 h-6 w-6" />
          </Button>
        </div>
      </main>

      <Footer />

      {/* PIN Keypad Modal */}
      {showPinKeypad && actionType && (
        <PinKeypad
          title={
            actionType === "CLOCK_IN" ? "Pointage Arrivée" : "Pointage Départ"
          }
          onSubmit={handlePinSubmit}
          onCancel={() => {
            setShowPinKeypad(false);
            setError("");
          }}
          error={error}
        />
      )}

      {/* Success Message */}
      {successData && (
        <SuccessMessage
          action={successData.action}
          employeeName={successData.employeeName}
          duration={successData.duration}
          time={successData.time}
          onClose={() => setSuccessData(null)}
        />
      )}
    </div>
  );
};
